import '../components/QrMenu.css';

export const CategoryCard = ({ category, colors, products, isOpen, onToggle }) => {
    // Verificamos que category exista
    if (!category) {
        console.error('CategoryCard: category es undefined');
        return null;
    }
    
    // Determinamos qué productos usar: los de category.products o los filtrados del prop products
    const hasOwnProducts = category.products && Array.isArray(category.products);
    
    console.log(`Renderizando CategoryCard para ${category.name} (ID: ${category.id})`, {
        isOpen,
        totalProducts: hasOwnProducts ? category.products.length : (products ? products.length : 0)
    });

    // Obtenemos los productos ya sea del objeto category o del prop products
    const categoryProducts = hasOwnProducts 
        ? category.products.filter(product => {
            // Verificar si product.category es un objeto o un ID
            const categoryId = typeof product.category === 'object' && product.category ?
                product.category.id : product.category_id;

            if (!categoryId) {
                console.warn('Producto sin categoría definida:', product);
            }

            return categoryId === category.id;
        })
        : products ? products.filter(product => {
            // Verificar si product.category es un objeto o un ID
            const categoryId = typeof product.category === 'object' && product.category ?
                product.category.id : product.category_id;

            if (!categoryId) {
                console.warn('Producto sin categoría definida:', product);
            }

            return categoryId === category.id;
        }) : [];

    console.log(`Productos en categoría ${category.name}:`, categoryProducts.length);

    if (categoryProducts.length === 0) {
        console.log(`Categoría ${category.name} vacía, no se muestra`);
        return null; // No mostrar categorías sin productos
    }

    // Agrupar productos por subcategoría si tienen subcategory_id
    const groupedProducts = {};
    console.log(`Agrupando ${categoryProducts.length} productos por subcategoría para ${category.name}`);

    categoryProducts.forEach(product => {
        if (product.subcategory_id) {
            if (!groupedProducts[product.subcategory_id]) {
                groupedProducts[product.subcategory_id] = [];
                console.log(`Creando grupo para subcategoría ID: ${product.subcategory_id}`);
            }
            groupedProducts[product.subcategory_id].push(product);
        } else {
            // Para productos sin subcategoría, usamos un ID especial
            if (!groupedProducts['no_subcategory']) {
                groupedProducts['no_subcategory'] = [];
                console.log(`Creando grupo para productos sin subcategoría`);
            }
            groupedProducts['no_subcategory'].push(product);
        }
    });

    console.log(`Grupos de productos formados para ${category.name}:`, Object.keys(groupedProducts));

    // Definir colores por defecto si no se proporcionan
    const themeColors = colors || {
        primary: '#8A1538',
        secondary: '#A98B60',
        accent: '#D7BE83',
        light: '#FFFFFF',
        dark: '#333333',
        background: '#F9F7F2',
    };

    console.log('Colores recibidos en CategoryCard:', themeColors);

    return (
        <div 
            className="overflow-visible rounded-xl shadow-md border transition-all hover:shadow-lg w-full" 
            style={{ 
                borderColor: themeColors.accent + '40',
                backgroundColor: themeColors.light
            }}
        >
            <button
                onClick={onToggle}
                className="w-full flex flex-wrap justify-between items-center p-4 text-white transition-all"
                style={{
                    background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.primary}dd)`
                }}
            >
                <h2 className="text-xl font-bold flex flex-wrap items-center justify-between flex-1">
                    <div className="flex items-center">
                        {category.icon && (
                            <span className="w-8 h-8 flex items-center justify-center mr-2 bg-white rounded-full" style={{ color: themeColors.primary }}>
                                {category.icon}
                            </span>
                        )}
                        <span className="break-words">{category.name}</span>
                    </div>
                    <span
                        className="ml-2 text-xs px-2 py-0.5 rounded-full"
                        style={{
                            backgroundColor: themeColors.light,
                            color: themeColors.primary
                        }}
                    >
                        {Object.values(groupedProducts).flat().length}
                    </span>
                </h2>
                <svg
                    className={`h-6 w-6 min-w-6 ml-2 transform transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="transition-all w-full"
                    style={{ backgroundColor: themeColors.light }}
                >
                    <div className="divide-y" style={{ borderColor: themeColors.accent + '20' }}>
                        {Object.entries(groupedProducts).map(([subcategoryId, products]) => (
                            <div key={subcategoryId} className="p-4">
                                {/* Mostrar título de subcategoría solo si existe y no es 'no_subcategory' */}
                                {subcategoryId !== 'no_subcategory' && (
                                    <h3 
                                        className="text-lg font-semibold mb-4 pb-2 border-b break-words"
                                        style={{ 
                                            color: themeColors.secondary,
                                            borderColor: themeColors.accent + '50'
                                        }}
                                    >
                                        {findSubcategoryName(subcategoryId, products)}
                                    </h3>
                                )}

                                <div className="space-y-4 w-full">
                                    {products.map(product => (
                                        <ProductCard key={product.id} product={product} colors={themeColors} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Función para encontrar el nombre de la subcategoría
const findSubcategoryName = (subcategoryId, products) => {
    // Intentamos encontrar el primer producto que tenga información de subcategoría
    const productWithSubcategoryInfo = products.find(
        product => product.subcategory && product.subcategory.name
    );

    if (productWithSubcategoryInfo && productWithSubcategoryInfo.subcategory) {
        return productWithSubcategoryInfo.subcategory.name;
    }

    return "Otros"; // Nombre por defecto si no se encuentra
};

export const ProductCard = ({ product, colors }) => {
    // Definir colores por defecto si no se proporcionan
    const themeColors = colors || {
        primary: '#8A1538',
        secondary: '#A98B60',
        accent: '#D7BE83',
        light: '#FFFFFF',
        dark: '#333333',
        background: '#F9F7F2',
    };
    
    // Usando el precio formateado directamente desde la API
    const formattedPrice = product.price_formatted || `$${(product.price_cents / 100).toFixed(2)}`;

    return (
        <div 
            className="flex flex-wrap justify-between items-start py-3 px-2 border-b last:border-0 rounded-lg transition-colors w-full"
            style={{ 
                borderColor: themeColors.accent + '30',
                ':hover': { backgroundColor: themeColors.accent + '10' }
            }}
        >
            <div className="flex-grow w-full sm:w-auto">
                <div className="flex flex-wrap items-start justify-between w-full">
                    <h3 className="font-semibold break-words mr-2 flex-grow" style={{ color: themeColors.dark }}>
                        {product.name}
                    </h3>
                    <div className="font-bold whitespace-nowrap text-right" style={{ color: themeColors.primary }}>
                        {formattedPrice}
                    </div>
                </div>
                {product.description && (
                    <p className="text-sm mt-1 pr-1 break-words" 
                       style={{ color: themeColors.dark + '99' }}>
                        {product.description}
                    </p>
                )}

                {/* Etiquetas adicionales como características del producto */}
                {product.image_url && (
                    <div className="flex mt-2">
                        <span 
                            className="text-xs px-2 py-1 rounded-md inline-flex items-center"
                            style={{ 
                                backgroundColor: themeColors.accent + '30',
                                color: themeColors.secondary
                            }}
                        >
                            <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="break-words">Con imagen</span>
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
